import { useState, type ReactNode } from 'react';
import { usePromiseTracker } from 'react-promise-tracker';
import { Button, IconButton } from 'rsuite';
import { MdDelete, MdSave } from 'react-icons/md';
import SelectPill from '../../../../../../components/SelectPill';
import { LoadingMenu } from '../../../../../../components/LoadingSpinner';
import { MULTI_FILTERS_TRACKER_AREA, SEGMENTS_TRACKER_AREA } from '../../constants';
import { useMultiFiltersSelection } from '../../hooks/useMultiFiltersSelection';
import { useManufacturerFilter } from '../../hooks/useManufacturerFilter';
import { useRegions } from '../../hooks/useRegions';
import { useSaveFilters } from '../../hooks/useSaveFilters';
import { useSegmentsFilter } from '../../hooks/useSegmentsFilter';
import { useVisaoGeralElasticidadeStore } from '../../VisaoGeralElasticidade.store';
import type { VisaoGeralElasticidadeMultiFilterType, VisaoGeralElasticidadeTableBody } from '../../types';
import { ModalSaveFilters } from '../ModalSaveFilters';
import { ProductSelector } from './ProductSelector';
import styles from './FiltersArea.module.scss';
import { buildMultiSelectPlaceholder } from '../../utils';

const applySavedFiltersToUi = (
  filtros: VisaoGeralElasticidadeTableBody,
  handlers: {
    setReferenceProduct: (value: string[]) => void;
    handleRegionSelect: (value: string) => void;
    applySavedMultiFilterValues: (name: VisaoGeralElasticidadeMultiFilterType, values: string[]) => void;
    applySavedManufacturerValues: (values: string[]) => void;
    handleSegmentSelect: (value: string) => void;
    handleSubsegmentSelect: (value: string) => void;
  },
) => {
  handlers.setReferenceProduct(filtros.productBarcodes ?? []);
  handlers.handleRegionSelect(filtros.region ?? '');
  handlers.applySavedMultiFilterValues('categories', filtros.categories);
  handlers.applySavedMultiFilterValues('sections', filtros.sections);
  handlers.applySavedMultiFilterValues('brands', filtros.brands);
  handlers.applySavedMultiFilterValues('contents', filtros.contents);
  handlers.applySavedManufacturerValues(filtros.manufacturers);

  if (filtros.segmentation) {
    handlers.handleSegmentSelect(filtros.segmentation);
  }

  if (filtros.subSegmentation) {
    handlers.handleSubsegmentSelect(filtros.subSegmentation);
  }
};

type FiltersAreaProps = {
  onClearQuickFilters?: () => void;
};

export const FiltersArea = ({ onClearQuickFilters }: FiltersAreaProps) => {
  const clearStoreFilters = useVisaoGeralElasticidadeStore.getState().clearFilters;

  const { region, regionOptions, filterHandlers: regionFilterHandlers } = useRegions();
  const {
    segmentation,
    subSegmentation,
    segmentOptions,
    subsegmentOptions,
    isSubsegmentDisabled,
    filterHandlers: segmentsFilterHandlers,
  } = useSegmentsFilter();
  const { categories, sections, brands, contents, filterHandlers } = useMultiFiltersSelection();
  const { manufacturers, filterHandlers: manufacturerFilterHandlers } = useManufacturerFilter();
  const {
    savedFiltersOptions,
    selectedSavedFilterId,
    showSaveModal,
    filterName,
    handleOpenSavedFilters,
    handleSearchSavedFilters,
    handleOpenSaveModal,
    handleCloseSaveModal,
    handleChangeFilterName,
    handleSaveFilter,
    handleSelectSavedFilter,
    handleClearSavedFilter,
    handleDeleteSavedFilter,
  } = useSaveFilters();
  const { promiseInProgress: isLoadingRegions } = usePromiseTracker({ area: 'elasticidade-regions' });
  const { promiseInProgress: isLoadingSegments } = usePromiseTracker({ area: SEGMENTS_TRACKER_AREA });
  const { promiseInProgress: isLoadingMultiFilters } = usePromiseTracker({ area: MULTI_FILTERS_TRACKER_AREA });

  const [referenceProduct, setReferenceProduct] = useState<string[]>([]);

  const renderMultiFilterSelect = (name: VisaoGeralElasticidadeMultiFilterType) => {
    const filterMap = {
      categories,
      sections,
      brands,
      contents,
    } as const;

    const filter = filterMap[name];

    return (
      <SelectPill
        id="check-picker-pill"
        type="check"
        placeholder={buildMultiSelectPlaceholder(name, filter.value)}
        data={filter.list}
        value={filter.value}
        onSelect={(value: string[]) => filterHandlers.handleSelectFilter(name, value)}
        onOpen={() => filterHandlers.handleOpen(name)}
        onSearch={(value: string) => filterHandlers.onSearchFilter(name, value)}
        onClean={() => filterHandlers.handleClearFilters(name)}
        filter={false}
        searchable
        cleanable
        locale={{
          noResultsText: isLoadingMultiFilters ? ' ' : 'Nenhum item encontrado',
          searchPlaceholder: 'Buscar item',
        }}
        renderMenu={(menu: ReactNode) => (
          <span>
            <LoadingMenu area={MULTI_FILTERS_TRACKER_AREA} />
            {menu}
          </span>
        )}
      />
    );
  };

  const handleClearFilters = () => {
    setReferenceProduct([]);
    regionFilterHandlers.handleClearFilters();
    handleClearSavedFilter();
    segmentsFilterHandlers.handleClearFilters();
    manufacturerFilterHandlers.handleClearFilters();
    filterHandlers.handleClearAllFilters();
    clearStoreFilters();
    onClearQuickFilters?.();
  };

  const handleApplySavedFilter = (id: number) => {
    const appliedFilters = handleSelectSavedFilter(id);
    if (!appliedFilters) return;

    applySavedFiltersToUi(appliedFilters, {
      setReferenceProduct,
      handleRegionSelect: regionFilterHandlers.handleRegionSelect,
      applySavedMultiFilterValues: filterHandlers.applySavedFilterValues,
      applySavedManufacturerValues: manufacturerFilterHandlers.applySavedFilterValues,
      handleSegmentSelect: segmentsFilterHandlers.handleSegmentSelect,
      handleSubsegmentSelect: segmentsFilterHandlers.handleSubsegmentSelect,
    });
  };

  return (
    <section className={styles.container} aria-label="Filtros">
      <div className={styles.box}>
        <div className={styles.filters}>
          <div className={styles['filters-row']}>
            <div className={styles['filters-row-top']}>
              <ProductSelector value={referenceProduct} onChange={setReferenceProduct} />
              <SelectPill
                id="select-picker-pill"
                placeholder="Segmento personalizado"
                data={segmentOptions}
                value={segmentation}
                onSelect={segmentsFilterHandlers.handleSegmentSelect}
                onOpen={segmentsFilterHandlers.handleSegmentOpen}
                onSearch={segmentsFilterHandlers.onSearchSegment}
                onClean={segmentsFilterHandlers.handleSegmentClear}
                filter={false}
                searchable
                cleanable
                locale={{
                  noResultsText: isLoadingSegments ? ' ' : 'Nenhum item encontrado',
                  searchPlaceholder: 'Buscar item',
                }}
                renderMenu={(menu: ReactNode) => (
                  <span>
                    <LoadingMenu area={SEGMENTS_TRACKER_AREA} />
                    {menu}
                  </span>
                )}
              />

              <SelectPill
                id="select-picker-pill"
                placeholder="Subsegmento personalizado"
                data={subsegmentOptions}
                value={subSegmentation}
                onSelect={segmentsFilterHandlers.handleSubsegmentSelect}
                onOpen={segmentsFilterHandlers.handleSubsegmentOpen}
                onSearch={segmentsFilterHandlers.onSearchSubsegment}
                onClean={segmentsFilterHandlers.handleSubsegmentClear}
                disabled={isSubsegmentDisabled}
                filter={false}
                searchable
                cleanable
                locale={{
                  noResultsText: isLoadingSegments ? ' ' : 'Nenhum item encontrado',
                  searchPlaceholder: 'Buscar item',
                }}
                renderMenu={(menu: ReactNode) => (
                  <span>
                    <LoadingMenu area={SEGMENTS_TRACKER_AREA} />
                    {menu}
                  </span>
                )}
              />

              <SelectPill
                id="select-picker-pill"
                placeholder="Região"
                data={regionOptions}
                value={region}
                onSelect={regionFilterHandlers.handleRegionSelect}
                onClean={regionFilterHandlers.handleRegionClear}
                onOpen={regionFilterHandlers.handleOpen}
                cleanable
                searchable={false}
                locale={{
                  noResultsText: isLoadingRegions ? ' ' : 'Nenhum item encontrado',
                }}
                renderMenu={(menu: ReactNode) => (
                  <span>
                    <LoadingMenu area="elasticidade-regions" />
                    {menu}
                  </span>
                )}
              />
              <SelectPill
                id="check-picker-pill"
                type="check"
                placeholder={buildMultiSelectPlaceholder('manufacturers', manufacturers.value)}
                data={manufacturers.list}
                value={manufacturers.value}
                onSelect={(value: string[]) => manufacturerFilterHandlers.handleSelectFilter(value)}
                onOpen={manufacturerFilterHandlers.handleOpen}
                onSearch={manufacturerFilterHandlers.onSearchFilter}
                onClean={manufacturerFilterHandlers.handleClearFilters}
                filter={false}
                searchable
                cleanable
                locale={{
                  noResultsText: isLoadingMultiFilters ? ' ' : 'Nenhum item encontrado',
                  searchPlaceholder: 'Buscar item',
                }}
                renderMenu={(menu: ReactNode) => (
                  <span>
                    <LoadingMenu area={MULTI_FILTERS_TRACKER_AREA} />
                    {menu}
                  </span>
                )}
              />
            </div>
            <Button className={styles['clear-btn']} onClick={handleClearFilters}>
              Limpar filtros
            </Button>
          </div>
        </div>

        <div className={styles['filters-row-bottom']}>
          <div className={styles['filters-row']}>
            {renderMultiFilterSelect('categories')}
            {renderMultiFilterSelect('sections')}
            {renderMultiFilterSelect('brands')}
            {renderMultiFilterSelect('contents')}
          </div>
          <div className={styles['filters-row-bottom-right']}>
            <IconButton className={styles['save-btn']} icon={<MdSave size={16} />} circle aria-label="Salvar filtros" onClick={handleOpenSaveModal} />
            <IconButton
              className={styles['delete-btn']}
              icon={<MdDelete size={16} />}
              circle
              aria-label="Deletar filtro"
              disabled={!selectedSavedFilterId}
              onClick={handleDeleteSavedFilter}
            />

            <SelectPill
              id="select-picker-pill"
              placeholder="Filtros salvos"
              placement="bottomEnd"
              data={savedFiltersOptions}
              value={selectedSavedFilterId}
              onOpen={handleOpenSavedFilters}
              onSearch={handleSearchSavedFilters}
              onSelect={(value: number) => handleApplySavedFilter(value)}
              onClean={handleClearSavedFilter}
              cleanable
              searchable
              className={styles.savedFiltersSelect}
              menuClassName={styles.savedFiltersMenu}
              preventOverflow
              locale={{
                noResultsText: 'Nenhum filtro encontrado',
                searchPlaceholder: 'Buscar filtro',
              }}
              renderMenu={(menu: ReactNode) => (
                <span>
                  <LoadingMenu area="elasticidade-saved-filters" />
                  {menu}
                </span>
              )}
            />
          </div>
        </div>
      </div>

      <ModalSaveFilters
        show={showSaveModal}
        filterName={filterName}
        onHide={handleCloseSaveModal}
        onChangeFilterName={handleChangeFilterName}
        onConfirm={handleSaveFilter}
      />
    </section>
  );
};
